import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, SealCheck, Clock, XCircle, Info, Lightning, Bank, Crown } from '@phosphor-icons/react';
import { parseInrInput, formatInrFriendly } from '../lib/inr';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { id: 'FUNDER', label: 'FUNDER', tagline: 'Fixed interest, fixed term', Icon: Lightning, color: 'cyan' },
  { id: 'PAPPA',  label: 'PAPPA',  tagline: 'Pre-Approved Purchase + markup', Icon: Bank, color: 'amber' },
  { id: 'KUBERA', label: 'KUBERA', tagline: 'Profit share on OTT-rights sale', Icon: Crown, color: 'purple' },
];

const PappaStatusBadge = ({ status }) => {
  const map = {
    pending:  { label: 'Pending admin verification', cls: 'bg-amber-500/10 text-amber-300 border-amber-500/30', Icon: Clock },
    active:   { label: 'Verified — live for investors', cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30', Icon: SealCheck },
    rejected: { label: 'Rejected', cls: 'bg-rose-500/10 text-rose-300 border-rose-500/30', Icon: XCircle },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-medium border ${cfg.cls}`} data-testid={`pappa-status-${status}`}>
      <Icon size={12} weight="bold" /> {cfg.label}
    </span>
  );
};

const FundingSetup = ({ user }) => {
  const { id: movieId } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [platform, setPlatform] = useState({ funder_tiers: [], kubera_default_share_pct: 30 });
  const [modes, setModes] = useState([]);
  const [funder, setFunder] = useState({ target_amount_inr: '' });
  const [pappa, setPappa] = useState({ ott_partner_name: '', ott_po_amount_inr: '', raise_amount_inr: '', markup_pct: 20, po_doc_url: '' });
  const [kubera, setKubera] = useState({ use_platform_default: true, ott_profit_share_pct: '', estimated_ott_sale_inr: '', target_amount_inr: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPo, setUploadingPo] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    (async () => {
      try {
        const [m, p] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/movies/${movieId}`),
          axios.get(`${BACKEND_URL}/api/funding/platform-config`),
        ]);
        setMovie(m.data);
        setPlatform(p.data);
        const cfg = m.data;
        setModes(cfg.funding_modes || []);
        if (cfg.funder) setFunder({ target_amount_inr: cfg.funder.target_amount_inr });
        if (cfg.pappa) setPappa({
          ott_partner_name: cfg.pappa.ott_partner_name || '',
          ott_po_amount_inr: cfg.pappa.ott_po_amount_inr || '',
          raise_amount_inr: cfg.pappa.raise_amount_inr || '',
          markup_pct: cfg.pappa.markup_pct ?? 20,
          po_doc_url: cfg.pappa.po_doc_url || '',
        });
        if (cfg.kubera) setKubera({
          use_platform_default: !!cfg.kubera.use_platform_default,
          ott_profit_share_pct: cfg.kubera.use_platform_default ? '' : (cfg.kubera.ott_profit_share_pct || ''),
          estimated_ott_sale_inr: cfg.kubera.estimated_ott_sale_inr || '',
          target_amount_inr: cfg.kubera.target_amount_inr || '',
        });
      } catch (err) {
        toast.error('Failed to load movie');
      } finally {
        setLoading(false);
      }
    })();
  }, [movieId, navigate, user]);

  const toggleMode = (m) => {
    setModes(prev => {
      if (prev.includes(m)) return prev.filter(x => x !== m);
      const next = [...prev, m];
      // Enforce rule: PAPPA + KUBERA cannot coexist
      if (next.includes('PAPPA') && next.includes('KUBERA')) {
        toast.error('PAPPA and KUBERA cannot be combined on the same movie');
        return prev;
      }
      if (next.length > 2) {
        toast.error('At most 2 funding modes per movie. You can combine FUNDER with PAPPA or KUBERA.');
        return prev;
      }
      return next;
    });
  };

  const uploadPo = async (file) => {
    if (!file) return;
    setUploadingPo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/uploads/producer-asset?kind=doc`, fd, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
      });
      setPappa(p => ({ ...p, po_doc_url: res.data.url }));
      toast.success('PO document uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPo(false);
    }
  };

  const previewRaise = () => {
    const po = parseInrInput(pappa.ott_po_amount_inr);
    if (!Number.isFinite(po) || po <= 0) return 0;
    const explicit = parseInrInput(pappa.raise_amount_inr);
    return Number.isFinite(explicit) && explicit > 0 ? explicit : Math.round(po * 0.8 * 100) / 100;
  };

  const previewMarkupPool = () => {
    const raise = previewRaise();
    const mk = Number(pappa.markup_pct) || 0;
    return Math.round(raise * mk) / 100;
  };

  const submit = async () => {
    if (modes.length === 0) return toast.error('Pick at least one funding mode');
    const body = { funding_modes: modes };

    if (modes.includes('FUNDER')) {
      const t = parseInrInput(funder.target_amount_inr);
      if (!Number.isFinite(t) || t <= 0) return toast.error('Enter a valid FUNDER target amount');
      body.funder = { target_amount_inr: t };
    }
    if (modes.includes('PAPPA')) {
      const po = parseInrInput(pappa.ott_po_amount_inr);
      if (!pappa.ott_partner_name.trim()) return toast.error('OTT partner name is required for PAPPA');
      if (!Number.isFinite(po) || po <= 0) return toast.error('Valid PAPPA PO amount required');
      const raise = pappa.raise_amount_inr ? parseInrInput(pappa.raise_amount_inr) : undefined;
      const mk = Number(pappa.markup_pct);
      if (!mk || mk <= 0 || mk > 50) return toast.error('PAPPA markup % must be between 0 and 50');
      if (!pappa.po_doc_url) return toast.error('Upload the OTT Purchase Order document for verification');
      body.pappa = {
        ott_partner_name: pappa.ott_partner_name.trim(),
        ott_po_amount_inr: po,
        markup_pct: mk,
        po_doc_url: pappa.po_doc_url,
        ...(raise !== undefined ? { raise_amount_inr: raise } : {}),
      };
    }
    if (modes.includes('KUBERA')) {
      const tgt = parseInrInput(kubera.target_amount_inr);
      if (!Number.isFinite(tgt) || tgt <= 0) return toast.error('Valid KUBERA target amount required');
      const k = { use_platform_default: !!kubera.use_platform_default, target_amount_inr: tgt };
      if (!kubera.use_platform_default) {
        const sp = Number(kubera.ott_profit_share_pct);
        if (!sp || sp <= 0 || sp > 100) return toast.error('KUBERA OTT profit-share % must be between 0 and 100');
        k.ott_profit_share_pct = sp;
      }
      if (kubera.estimated_ott_sale_inr) {
        const est = parseInrInput(kubera.estimated_ott_sale_inr);
        if (Number.isFinite(est) && est > 0) k.estimated_ott_sale_inr = est;
      }
      body.kubera = k;
    }

    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/movies/${movieId}/funding-config`, body, { headers: headers() });
      toast.success('Funding configuration saved');
      navigate(`/movie/${movieId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !movie) return <div className="min-h-screen bg-background flex items-center justify-center text-slate-400 font-body">Loading…</div>;

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />
      <nav data-web-chrome="nav" className="relative z-10 glass-card sticky top-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate(`/movie/${movieId}`)} className="flex items-center gap-2 text-slate-300 hover:text-white" data-testid="back-to-movie">
            <ArrowLeft size={18} /> <span className="font-body text-sm">Back to movie</span>
          </button>
        </div>
      </nav>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <p className="font-body text-sm text-slate-400 uppercase tracking-wide mb-1">Funding setup for</p>
          <h1 className="font-heading text-4xl font-bold mb-2">{movie.title}</h1>
          <p className="font-body text-slate-400">Choose how you want to raise capital. Pick one mode, or combine FUNDER with either PAPPA or KUBERA. PAPPA and KUBERA cannot be combined.</p>
        </div>

        {/* Mode picker */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="modes-picker">
          {MODES.map(m => {
            const selected = modes.includes(m.id);
            const Icon = m.Icon;
            const disabled =
              (m.id === 'PAPPA' && modes.includes('KUBERA')) ||
              (m.id === 'KUBERA' && modes.includes('PAPPA')) ||
              (!selected && modes.length >= 2);
            return (
              <button
                key={m.id}
                onClick={() => !disabled && toggleMode(m.id)}
                disabled={disabled}
                data-testid={`mode-card-${m.id}`}
                className={`text-left p-5 rounded-lg border transition-all ${
                  selected
                    ? 'bg-gold/10 border-gold ring-2 ring-gold/30'
                    : disabled
                      ? 'bg-card border-white/5 opacity-40 cursor-not-allowed'
                      : 'bg-card border-white/10 hover:border-white/20'
                }`}
              >
                <Icon size={28} weight="bold" className={selected ? 'text-gold' : 'text-slate-400'} />
                <h3 className="font-heading text-lg font-bold mt-3">{m.label}</h3>
                <p className="font-body text-xs text-slate-400 mt-1">{m.tagline}</p>
                {selected && <span className="mt-3 inline-block text-[10px] uppercase tracking-wide text-gold font-bold">Selected</span>}
              </button>
            );
          })}
        </section>

        {/* FUNDER config */}
        {modes.includes('FUNDER') && (
          <section className="bg-card border border-white/10 rounded-lg p-6 mb-6" data-testid="funder-section">
            <div className="flex items-center gap-2 mb-3">
              <Lightning size={20} weight="bold" className="text-cyan-400" />
              <h2 className="font-heading text-xl font-bold">FUNDER — fixed-interest debt</h2>
            </div>
            <p className="font-body text-sm text-slate-400 mb-4">Investors pick from the platform-defined tier grid below. They receive principal + interest after the chosen term.</p>

            <div className="mb-4">
              <label className="font-body text-xs text-slate-400">Total FUNDER target (INR) *</label>
              <input data-testid="funder-target" required className="admin-input mt-1" value={funder.target_amount_inr} onChange={e => setFunder({ target_amount_inr: e.target.value })} placeholder="e.g., 5 crore or 50000000" />
            </div>

            <div className="bg-white/[0.03] border border-white/10 rounded p-4">
              <div className="flex items-center gap-2 mb-3 text-xs text-slate-400 font-body"><Info size={14} /> Platform-defined tier grid (read-only)</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {platform.funder_tiers.map(t => (
                  <div key={t.name} className="bg-black/30 rounded p-3 border border-white/5" data-testid={`platform-tier-${t.name}`}>
                    <div className="flex items-baseline justify-between">
                      <span className="font-heading font-bold">{t.name}</span>
                      <span className="font-mono text-sm text-cyan-300">{t.interest_rate_pct}% p.a.</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">Min ₹{formatInrFriendly(t.min_amount_inr)} · {t.term_months} months</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* PAPPA config */}
        {modes.includes('PAPPA') && (
          <section className="bg-card border border-white/10 rounded-lg p-6 mb-6" data-testid="pappa-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
              <Bank size={20} weight="bold" className="text-amber-400" />
                <h2 className="font-heading text-xl font-bold">PAPPA — Pre-Approved Purchase Profit Associates</h2>
              </div>
              {movie.pappa?.status && <PappaStatusBadge status={movie.pappa.status} />}
            </div>
            <p className="font-body text-sm text-slate-400 mb-2">You have a Purchase Order from an OTT. Raise a discounted slice (default 80% of PO) and return it to associates with a markup once OTT pays.</p>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded p-3 text-xs text-amber-200 font-body mb-4">
              <Info size={12} className="inline mr-1" /> The PAPPA track will stay <strong>pending</strong> until an admin verifies your PO document. Investors will only see it once verified.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-body text-xs text-slate-400">OTT partner / buyer name *</label>
                <input data-testid="pappa-partner" className="admin-input mt-1" value={pappa.ott_partner_name} onChange={e => setPappa({ ...pappa, ott_partner_name: e.target.value })} placeholder="e.g., Netflix India, Prime Video, JioCinema" />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">OTT Purchase Order amount (100%) *</label>
                <input data-testid="pappa-po-amount" className="admin-input mt-1" value={pappa.ott_po_amount_inr} onChange={e => setPappa({ ...pappa, ott_po_amount_inr: e.target.value })} placeholder="e.g., 10 crore" />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Amount to raise from associates (defaults to 80% of PO)</label>
                <input data-testid="pappa-raise-amount" className="admin-input mt-1" value={pappa.raise_amount_inr} onChange={e => setPappa({ ...pappa, raise_amount_inr: e.target.value })} placeholder={`Auto: ₹${formatInrFriendly(previewRaise())}`} />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Markup % distributed back to associates *</label>
                <input data-testid="pappa-markup" type="number" min="1" max="50" step="0.5" className="admin-input mt-1" value={pappa.markup_pct} onChange={e => setPappa({ ...pappa, markup_pct: e.target.value })} />
                <p className="text-[10px] text-slate-400 mt-1">Default 20%. Higher = more attractive to associates, smaller margin for you.</p>
              </div>
              <div className="md:col-span-2">
                <label className="font-body text-xs text-slate-400">Purchase Order document (PDF/image, max 15MB) *</label>
                <div className="mt-1 flex items-center gap-2">
                  <input data-testid="pappa-po-upload" type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => uploadPo(e.target.files?.[0])} className="text-xs flex-1" />
                  {uploadingPo && <span className="text-xs text-slate-400">Uploading…</span>}
                </div>
                {pappa.po_doc_url && (
                  <a data-testid="pappa-po-uploaded" href={`${BACKEND_URL}${pappa.po_doc_url}`} target="_blank" rel="noreferrer" className="text-xs text-gold hover:underline mt-1 inline-block">PO document attached ↗</a>
                )}
              </div>
            </div>

            {/* Live preview */}
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pappa-preview">
              <div className="bg-white/[0.03] border border-white/10 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">Investors put in</div>
                <div className="font-mono text-lg font-bold tabular-nums text-amber-300">₹{formatInrFriendly(previewRaise())}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">Markup pool (paid out)</div>
                <div className="font-mono text-lg font-bold tabular-nums text-emerald-400">+₹{formatInrFriendly(previewMarkupPool())}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">Producer keeps from PO</div>
                <div className="font-mono text-lg font-bold tabular-nums text-cyan-400">₹{formatInrFriendly(Math.max(parseInrInput(pappa.ott_po_amount_inr || 0) - previewRaise() - previewMarkupPool(), 0))}</div>
              </div>
            </div>
            {movie.pappa?.review_notes && (
              <p className="text-xs text-amber-200 italic mt-3" data-testid="pappa-review-notes">Admin note: {movie.pappa.review_notes}</p>
            )}
          </section>
        )}

        {/* KUBERA config */}
        {modes.includes('KUBERA') && (
          <section className="bg-card border border-white/10 rounded-lg p-6 mb-6" data-testid="kubera-section">
            <div className="flex items-center gap-2 mb-3">
              <Crown size={20} weight="bold" className="text-purple-400" />
              <h2 className="font-heading text-xl font-bold">KUBERA — OTT-rights profit share</h2>
            </div>
            <p className="font-body text-sm text-slate-400 mb-4">Investors get a share of the OTT-rights sale only. No claim on theatrical, music, or merchandise revenue.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="font-body text-sm font-medium flex items-center gap-2">
                  <input data-testid="kubera-use-default-toggle" type="checkbox" checked={!!kubera.use_platform_default} onChange={e => setKubera({ ...kubera, use_platform_default: e.target.checked })} />
                  Use platform default OTT-share ({platform.kubera_default_share_pct}%)
                </label>
              </div>
              {!kubera.use_platform_default && (
                <div>
                  <label className="font-body text-xs text-slate-400">Custom OTT profit-share % *</label>
                  <input data-testid="kubera-share-pct" type="number" min="1" max="100" step="0.5" className="admin-input mt-1" value={kubera.ott_profit_share_pct} onChange={e => setKubera({ ...kubera, ott_profit_share_pct: e.target.value })} placeholder="e.g., 25" />
                </div>
              )}
              <div>
                <label className="font-body text-xs text-slate-400">Target raise from KUBERA pool (INR) *</label>
                <input data-testid="kubera-target" className="admin-input mt-1" value={kubera.target_amount_inr} onChange={e => setKubera({ ...kubera, target_amount_inr: e.target.value })} placeholder="e.g., 2 crore" />
              </div>
              <div className="md:col-span-2">
                <label className="font-body text-xs text-slate-400">Estimated OTT sale value (optional — shown to investors)</label>
                <input data-testid="kubera-estimated-sale" className="admin-input mt-1" value={kubera.estimated_ott_sale_inr} onChange={e => setKubera({ ...kubera, estimated_ott_sale_inr: e.target.value })} placeholder="e.g., 8 crore (helps investors model upside)" />
              </div>
            </div>
          </section>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={() => navigate(`/movie/${movieId}`)} className="btn-secondary text-sm py-2">Cancel</button>
          <button data-testid="funding-save-btn" onClick={submit} disabled={saving} className="btn-primary text-sm py-2">{saving ? 'Saving…' : 'Save funding setup'}</button>
        </div>
      </div>
    </div>
  );
};

export default FundingSetup;
